import type { PreviewRow, ReassignmentRowState } from './types';
import type { AnnotatorBatchBreakdown } from '../../types/response';

/** Gates the entity-accurate, ordered (prior-rows-only) movable-count behavior + reallocation toast message and severity update. */
export const REALLOCATION_ORDERED_MOVABLE_COUNT_FF = 'cp_reallocation_ordered_movable_count_fix';

/**
 * Gates ignoring draft (not-yet-submitted) sibling reallocation rows when computing
 * how many encounters can move FROM a given annotator: ON bounds the cap by that
 * annotator's currently allocated (DB-committed) pending count only; OFF (default)
 * keeps letting other draft rows that move encounters into that annotator inflate it.
 */
export const ANNOTATION_PENDING_LIMIT_COUNT_FF = 'cp_annotation_pending_limit_count_fix';

/** A row contributes to the allocation preview once From, To and a positive count are set. */
export const isActiveRow = (row: ReassignmentRowState): boolean =>
  Boolean(row.fromAnnotatorId && row.toAnnotatorId && row.count && row.count > 0);

const pendingOf = (perAnnotator: AnnotatorBatchBreakdown[], annotatorId: string): number =>
  perAnnotator.find((a) => a.annotatorId === annotatorId)?.pendingCount ?? 0;

const sumCounts = (rows: ReassignmentRowState[]): number =>
  rows.reduce((sum, r) => sum + (r.count ?? 0), 0);

/**
 * Maximum number of encounters this row may move.
 *
 * Gated by `cp_reallocation_ordered_movable_count_fix` (`orderedFixEnabled`):
 *  - ON: trusts `row.movable` directly — it's already sibling-aware and
 *    entity-accurate (see ReassignmentRow's `effectivePending` and the
 *    backend's context-aware movable-count, both scoped to rows *before*
 *    this one). Falls back to the plain pending count if not yet known.
 *  - OFF (default, legacy): re-derives a flat sibling outflow/inflow bound
 *    from ALL other rows regardless of order, then takes Math.min against
 *    `row.movable` — the original pre-fix behavior.
 *
 * Independently gated by `cp_annotation_pending_limit_count_fix`
 * (`pendingLimitFixEnabled`): when ON, the legacy branch's sibling-inflow term
 * is dropped — a draft row moving encounters INTO `row.fromAnnotatorId` never
 * grows its cap, which stays bounded by the currently allocated pending count.
 * When OFF (default), that inflow still inflates the cap (the reported bug).
 */
export const maxForRow = (
  rows: ReassignmentRowState[],
  row: ReassignmentRowState,
  perAnnotator: AnnotatorBatchBreakdown[],
  orderedFixEnabled: boolean,
  pendingLimitFixEnabled: boolean
): number => {
  if (orderedFixEnabled) {
    if (row.movable != null) return Math.max(0, row.movable);
    return Math.max(0, pendingOf(perAnnotator, row.fromAnnotatorId));
  }

  const others = rows.filter((r) => r.id !== row.id);
  const usedSameFrom = sumCounts(others.filter((r) => r.fromAnnotatorId === row.fromAnnotatorId));
  const incomingToFrom = pendingLimitFixEnabled
    ? 0
    : sumCounts(others.filter((r) => isActiveRow(r) && r.toAnnotatorId === row.fromAnnotatorId));
  const maxByPending = pendingOf(perAnnotator, row.fromAnnotatorId) - usedSameFrom + incomingToFrom;

  if (row.movable == null) {
    return Math.max(0, maxByPending);
  }
  return Math.max(0, Math.min(maxByPending, row.movable));
};

/** True when the row's count exceeds what it is allowed to move (the red error state). */
export const rowHasError = (
  rows: ReassignmentRowState[],
  row: ReassignmentRowState,
  perAnnotator: AnnotatorBatchBreakdown[],
  orderedFixEnabled: boolean,
  pendingLimitFixEnabled: boolean
): boolean => {
  if (row.count == null) return false;
  if (row.count < 0) return true;
  if (!row.fromAnnotatorId) return false;
  return row.count > maxForRow(rows, row, perAnnotator, orderedFixEnabled, pendingLimitFixEnabled);
};

/** Net change in an annotator's pending count across all active rows. */
export const deltaForAnnotator = (rows: ReassignmentRowState[], annotatorId: string): number =>
  rows.filter(isActiveRow).reduce((delta, r) => {
    if (r.toAnnotatorId === annotatorId) return delta + (r.count ?? 0);
    if (r.fromAnnotatorId === annotatorId) return delta - (r.count ?? 0);
    return delta;
  }, 0);

const nameOf = (perAnnotator: AnnotatorBatchBreakdown[], id: string): string =>
  perAnnotator.find((a) => a.annotatorId === id)?.annotatorName ?? '';

export interface ActiveMove {
  count: number;
  fromName: string;
  toName: string;
}

/** The configured, complete moves for a batch, resolved to display names. */
export const activeMoves = (
  rows: ReassignmentRowState[],
  perAnnotator: AnnotatorBatchBreakdown[]
): ActiveMove[] =>
  rows.filter(isActiveRow).map((r) => ({
    count: r.count as number,
    fromName: nameOf(perAnnotator, r.fromAnnotatorId),
    toName: nameOf(perAnnotator, r.toAnnotatorId),
  }));

/** Build the preview-table rows for a batch from its breakdown + the configured rows. */
export const computePreviewRows = (
  perAnnotator: AnnotatorBatchBreakdown[],
  rows: ReassignmentRowState[]
): PreviewRow[] =>
  perAnnotator.map((a) => {
    const pendingBefore = a.pendingCount;
    const deltaPending = deltaForAnnotator(rows, a.annotatorId);
    const totalBefore = a.pendingCount + a.draftCount + a.finalisedCount;
    return {
      annotatorId: a.annotatorId,
      annotatorName: a.annotatorName,
      isAiAgent: Boolean(a.isAiAgent),
      pendingBefore,
      deltaPending,
      pendingAfter: pendingBefore + deltaPending,
      draftCount: a.draftCount,
      finalisedCount: a.finalisedCount,
      totalBefore,
      totalAfter: totalBefore + deltaPending,
    };
  });
